import { Capacitor } from "@capacitor/core";
import { api } from "@/shared/api/client";

/**
 * 네이티브(안드로이드/iOS) 푸시 등록 — 백엔드 푸시 루프의 마지막 반쪽.
 *
 * 백엔드(`safety/services/notify/push.py`)는 이미 `NotificationPreference.devices`
 * 의 `{token, platform, label}` 목록으로 FCM/APNs 를 쏜다. 지금까지 그 목록에
 * 토큰을 넣는 주체가 없어 푸시가 끝단에서 끊겨 있었다. 이 모듈이 네이티브에서
 * FCM/APNs 토큰을 받아 그 목록에 등록해 루프를 닫는다.
 *
 * 웹(PWA 브라우저)에서는 `isNativePlatform()` 이 false 라 전부 no-op — 웹 번들은
 * 영향 없고, 실제 네이티브 플러그인 코드는 동적 import 라 웹에 섞여 들어가지 않는다.
 */

interface DeviceToken {
  token: string;
  platform: string;
  label?: string;
}

/**
 * 푸시 활성 여부 — **기본 꺼짐**.
 *
 * ⚠️ Android 에서 `PushNotifications.register()` 는 google-services.json 이 없으면
 * `IllegalStateException: Default FirebaseApp is not initialized` 를 **네이티브
 * 스레드에서** 던진다. 이건 JS try/catch 로 못 잡고 앱이 그대로 죽는다
 * (실측: 로그인 직후 FATAL EXCEPTION → 프로세스 종료).
 *
 * 그래서 자격이 갖춰졌을 때만 켠다. 켜는 조건:
 *   · Android — `android/app/google-services.json` 배치
 *   · iOS     — APNs 키 + Xcode Push Notifications capability
 * 둘 다 준비되면 빌드에 `VITE_ENABLE_PUSH=1` 을 준다.
 *
 * 꺼져 있어도 인앱 WebSocket 경보는 그대로 동작한다 — 잃는 것은 백그라운드
 * 알림뿐이고, 얻는 것은 "앱이 죽지 않는다"이다.
 */
const PUSH_ENABLED = import.meta.env.VITE_ENABLE_PUSH === "1";

export async function initNativePush(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  if (!PUSH_ENABLED) return;

  const { PushNotifications } = await import("@capacitor/push-notifications");

  let perm = await PushNotifications.checkPermissions();
  if (perm.receive === "prompt" || perm.receive === "prompt-with-rationale") {
    perm = await PushNotifications.requestPermissions();
  }
  if (perm.receive !== "granted") return;

  await PushNotifications.removeAllListeners();

  // FCM(안드로이드)·APNs(iOS) 토큰 수신 → 서버 등록.
  PushNotifications.addListener("registration", (token) => {
    void registerDeviceToken(token.value, Capacitor.getPlatform());
  });
  PushNotifications.addListener("registrationError", () => {
    /* 토큰 등록 실패로 앱을 막지 않는다 — 인앱 WebSocket 경보는 계속 동작 */
  });
  // 알림 탭 → 경보 화면으로. (해시 라우터가 아니어도 안전한 최소 이동)
  PushNotifications.addListener("pushNotificationActionPerformed", () => {
    try {
      if (location.pathname !== "/alerts") location.assign("/alerts");
    } catch {
      /* noop */
    }
  });

  try {
    await PushNotifications.register();
  } catch {
    /* JS 로 전달되는 실패만 여기서 삼킨다. 네이티브 FATAL 은 위 게이트로 막는다. */
  }
}

/** 현재 devices 목록을 읽어 이 토큰을 dedupe 추가한 뒤 PUT. push_enabled 도 켠다. */
async function registerDeviceToken(token: string, platform: string): Promise<void> {
  try {
    const pref = (await api.get<{ devices?: DeviceToken[] }>("/notifications/preferences/")).data;
    const devices: DeviceToken[] = Array.isArray(pref.devices) ? pref.devices : [];
    if (devices.some((d) => d?.token === token)) return; // 이미 등록됨
    const next = [...devices, { token, platform, label: `${platform} app` }];
    await api.put("/notifications/preferences/me/", { devices: next, push_enabled: true });
  } catch {
    /* 서버 등록 실패는 조용히 넘긴다 — 다음 실행 시 재시도된다 */
  }
}
